export type LiveDemoConfig = {
  homeBrowseChance: number;
  homeBrowseMinMs: number;
  homeBrowseMaxMs: number;
  homeQuickMinMs: number;
  homeQuickMaxMs: number;
  maxBrowsePage: number;
  playerGoHomeChance: number;
  seriesPauseMs: number;
  subtitleScrollChance: number;
  subtitleScrollActionsMin: number;
  subtitleScrollActionsMax: number;
  homeScrollChance: number;
  homeScrollActionsMin: number;
  homeScrollActionsMax: number;
  aiPerVideoCount: number;
  blockedVideoIds: string[];
  blockedVideoKeywords: string[];
};

export const LIVE_DEMO_CONFIG_KEY = "live-demo:config:v1";

export const DEFAULT_LIVE_DEMO_CONFIG: LiveDemoConfig = {
  homeBrowseChance: 0.55,
  homeBrowseMinMs: 6000,
  homeBrowseMaxMs: 10500,
  homeQuickMinMs: 3000,
  homeQuickMaxMs: 5500,
  maxBrowsePage: 3,
  playerGoHomeChance: 0.35,
  seriesPauseMs: 10000,
  subtitleScrollChance: 0.55,
  subtitleScrollActionsMin: 1,
  subtitleScrollActionsMax: 2,
  homeScrollChance: 0.6,
  homeScrollActionsMin: 1,
  homeScrollActionsMax: 3,
  aiPerVideoCount: 1,
  blockedVideoIds: [],
  blockedVideoKeywords: [],
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeTitle = (value: unknown) =>
  normalizeText(value)
    .replace(/\s+/g, "")
    .replace(/[·•．。\-_/\\]/g, "");

function sanitizeStringList(raw: unknown, maxLen: number) {
  if (!Array.isArray(raw)) return [] as string[];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const v = normalizeText(item);
    if (!v || seen.has(v)) continue;
    out.push(v);
    seen.add(v);
    if (out.length >= maxLen) break;
  }
  return out;
}

export function parseLiveConfigListInput(raw: string) {
  return raw
    .split(/[\n,，;；]+/g)
    .map((item) => normalizeText(item))
    .filter(Boolean);
}

export function formatLiveConfigListInput(list: string[]) {
  return (list || []).join("\n");
}

export function isLiveDemoBlockedVideo(
  config: Partial<LiveDemoConfig> | undefined,
  videoId: unknown,
  title: unknown,
) {
  const ids = sanitizeStringList((config as any)?.blockedVideoIds, 500);
  const keywords = sanitizeStringList((config as any)?.blockedVideoKeywords, 200);

  const normalizedId = normalizeText(videoId);
  if (normalizedId && ids.includes(normalizedId)) {
    return true;
  }

  const normalizedTitle = normalizeTitle(title);
  if (!normalizedTitle || keywords.length === 0) {
    return false;
  }

  return keywords.some((kw) => normalizedTitle.includes(normalizeTitle(kw)));
}

export function sanitizeLiveDemoConfig(raw: Partial<LiveDemoConfig>): LiveDemoConfig {
  const next = { ...DEFAULT_LIVE_DEMO_CONFIG, ...raw };
  return {
    homeBrowseChance: clamp(Number(next.homeBrowseChance || 0), 0, 1),
    homeBrowseMinMs: Math.round(clamp(Number(next.homeBrowseMinMs || 0), 1000, 60000)),
    homeBrowseMaxMs: Math.round(clamp(Number(next.homeBrowseMaxMs || 0), 1000, 90000)),
    homeQuickMinMs: Math.round(clamp(Number(next.homeQuickMinMs || 0), 500, 30000)),
    homeQuickMaxMs: Math.round(clamp(Number(next.homeQuickMaxMs || 0), 500, 60000)),
    maxBrowsePage: Math.round(clamp(Number(next.maxBrowsePage || 0), 1, 10)),
    playerGoHomeChance: clamp(Number(next.playerGoHomeChance || 0), 0, 1),
    seriesPauseMs: Math.round(clamp(Number(next.seriesPauseMs || 0), 2000, 30000)),
    subtitleScrollChance: clamp(Number(next.subtitleScrollChance || 0), 0, 1),
    subtitleScrollActionsMin: Math.round(clamp(Number(next.subtitleScrollActionsMin || 0), 0, 6)),
    subtitleScrollActionsMax: Math.round(clamp(Number(next.subtitleScrollActionsMax || 0), 0, 8)),
    homeScrollChance: clamp(Number(next.homeScrollChance || 0), 0, 1),
    homeScrollActionsMin: Math.round(clamp(Number(next.homeScrollActionsMin || 0), 0, 8)),
    homeScrollActionsMax: Math.round(clamp(Number(next.homeScrollActionsMax || 0), 0, 10)),
    aiPerVideoCount: Math.round(clamp(Number(next.aiPerVideoCount || 0), 0, 3)),
    blockedVideoIds: sanitizeStringList((next as any).blockedVideoIds, 500),
    blockedVideoKeywords: sanitizeStringList((next as any).blockedVideoKeywords, 200),
  };
}

export function readLiveDemoConfig(): LiveDemoConfig {
  if (typeof window === "undefined") return DEFAULT_LIVE_DEMO_CONFIG;
  try {
    const raw = window.localStorage.getItem(LIVE_DEMO_CONFIG_KEY);
    if (!raw) return DEFAULT_LIVE_DEMO_CONFIG;
    const parsed = JSON.parse(raw) as Partial<LiveDemoConfig>;
    return sanitizeLiveDemoConfig(parsed);
  } catch {
    return DEFAULT_LIVE_DEMO_CONFIG;
  }
}

export function saveLiveDemoConfig(config: Partial<LiveDemoConfig>) {
  if (typeof window === "undefined") return;
  const safe = sanitizeLiveDemoConfig(config);
  window.localStorage.setItem(LIVE_DEMO_CONFIG_KEY, JSON.stringify(safe));
}
